import { localApiClient } from "./localApiClient";

const currentHospitalId = () =>
  sessionStorage.getItem("hospitalId") ||
  localStorage.getItem("hospitalId") ||
  "";

const toStatus = (value) => {
  const status = String(value || "").toUpperCase();
  return status === "INACTIVE" || status === "INACTIVE" ? "INACTIVE" : "ACTIVE";
};

const fromStatus = (value) => (String(value || "").toUpperCase() === "INACTIVE" ? "Inactive" : "Active");

const toInt = (value) => {
  if (value === "" || value === undefined || value === null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, Math.round(parsed))) : undefined;
};

const inferType = (scheme = {}) => {
  const raw = String(scheme.type || scheme.category || scheme.name || "").toUpperCase();
  if (raw.includes("NHIA")) return "NHIA";
  if (raw.includes("HMO")) return "HMO";
  if (raw.includes("STAFF")) return "STAFF";
  if (raw.includes("PRIVATE")) return "PRIVATE";
  return "OTHER";
};

const planByName = (plans = [], name) =>
  plans.find((plan) => String(plan.name || "").toLowerCase() === String(name).toLowerCase()) ||
  plans.find((plan) => String(plan.planType || "").toLowerCase() === String(name).toLowerCase());

export const fromLocalScheme = (scheme = {}) => {
  const plans = Array.isArray(scheme.plans) ? scheme.plans : [];
  const publicPlan = planByName(plans, "Public") || plans[0] || {};
  const privatePlan = planByName(plans, "Private") || plans[0] || {};
  return {
    ...scheme,
    id: scheme.id,
    name: scheme.name || "",
    code: scheme.code || "",
    category: scheme.type || scheme.category || "OTHER",
    type: scheme.type || "OTHER",
    status: fromStatus(scheme.status),
    isPredefined: false,
    publicServicePercentage: scheme.publicServicePercentage ?? publicPlan.serviceCoveragePercent ?? 0,
    publicDrugPercentage: scheme.publicDrugPercentage ?? publicPlan.drugCoveragePercent ?? 0,
    privateServicePercentage: scheme.privateServicePercentage ?? privatePlan.serviceCoveragePercent ?? 0,
    privateDrugPercentage: scheme.privateDrugPercentage ?? privatePlan.drugCoveragePercent ?? 0,
    plans,
  };
};

const toLocalSchemePayload = (hospitalId, scheme = {}) => ({
  hospitalId,
  name: String(scheme.name || "").trim(),
  code: scheme.code || undefined,
  type: inferType(scheme),
  status: toStatus(scheme.status),
  contactName: scheme.contactName || scheme.contactPerson || undefined,
  phone: scheme.phone || undefined,
  email: scheme.email || undefined,
  address: scheme.address || undefined,
  publicServicePercentage: toInt(scheme.publicServicePercentage),
  publicDrugPercentage: toInt(scheme.publicDrugPercentage),
  privateServicePercentage: toInt(scheme.privateServicePercentage),
  privateDrugPercentage: toInt(scheme.privateDrugPercentage),
  metadata: scheme.metadata || undefined,
});

const wrapLocalError = (action, error) => {
  const message = error?.message || String(error);
  if (/bearer|jwt|unauthor/i.test(message)) {
    throw new Error(`${action} failed: Local API token is missing or expired.`);
  }
  if (/permission|forbidden/i.test(message)) {
    throw new Error(`${action} failed: Your local role does not have scheme access.`);
  }
  if (/hospital|scope/i.test(message)) {
    throw new Error(`${action} failed: Hospital scope mismatch.`);
  }
  if (/failed to fetch|network/i.test(message)) {
    throw new Error(`${action} failed: Local API is unavailable.`);
  }
  throw new Error(`${action} failed: ${message}`);
};

export const localSchemeService = {
  async saveCustomScheme(hospitalId, scheme = {}) {
    try {
      if (!hospitalId || !scheme?.name) throw new Error("Hospital ID and scheme name are required.");
      const payload = toLocalSchemePayload(hospitalId, scheme);
      const saved = scheme.id
        ? await localApiClient.patch(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(scheme.id)}`, payload)
        : await localApiClient.post("/schemes", payload);
      return { success: true, message: "scheme saved successfully", id: saved.id, data: fromLocalScheme(saved) };
    } catch (error) {
      wrapLocalError("Save scheme", error);
    }
  },

  async fetchCustomScheme(hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const rows = await localApiClient.get(`/schemes/${encodeURIComponent(hospitalId)}`);
      return (Array.isArray(rows) ? rows : []).map(fromLocalScheme);
    } catch (error) {
      wrapLocalError("Fetch schemes", error);
    }
  },

  async fetchCustomSchemeActive(hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const rows = await localApiClient.get(`/schemes/${encodeURIComponent(hospitalId)}`, { status: "ACTIVE" });
      return (Array.isArray(rows) ? rows : []).map(fromLocalScheme);
    } catch (error) {
      wrapLocalError("Fetch active schemes", error);
    }
  },

  async fetchPredefinedScheme() {
    return this.fetchCustomScheme(currentHospitalId());
  },

  async fetchPredefinedSchemeActive() {
    return this.fetchCustomSchemeActive(currentHospitalId());
  },

  async fetchSchemeById(hospitalId = currentHospitalId(), schemeId) {
    try {
      if (!hospitalId || !schemeId) throw new Error("Hospital ID and scheme ID are required.");
      const scheme = await localApiClient.get(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}`);
      return fromLocalScheme(scheme);
    } catch (error) {
      wrapLocalError("Fetch scheme", error);
    }
  },

  async toggleSchemeStatus(schemeId, hospitalId = currentHospitalId()) {
    try {
      const scheme = await this.fetchSchemeById(hospitalId, schemeId);
      const next = scheme.status === "Active" ? "INACTIVE" : "ACTIVE";
      await localApiClient.patch(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}`, { status: next });
      return { success: true };
    } catch (error) {
      wrapLocalError("Toggle scheme status", error);
    }
  },

  async deleteCustomScheme(schemeId, hospitalId = currentHospitalId()) {
    try {
      if (!hospitalId || !schemeId) throw new Error("Hospital ID and scheme ID are required.");
      await localApiClient.delete(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}`);
      return { success: true, message: "scheme deleted successfully" };
    } catch (error) {
      wrapLocalError("Disable scheme", error);
    }
  },

  async createPlan(hospitalId, schemeId, plan = {}) {
    try {
      return await localApiClient.post(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/plans`, {
        name: plan.name,
        planType: plan.planType,
        serviceCoveragePercent: toInt(plan.serviceCoveragePercent),
        drugCoveragePercent: toInt(plan.drugCoveragePercent),
        patientCopayPercent: toInt(plan.patientCopayPercent),
        status: toStatus(plan.status),
        metadata: plan.metadata,
      });
    } catch (error) {
      wrapLocalError("Create scheme plan", error);
    }
  },

  listPlans(hospitalId, schemeId) {
    return localApiClient.get(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/plans`);
  },

  calculateCoverage(payload = {}) {
    return localApiClient.post("/schemes/calculate-coverage", payload);
  },

  createServiceOverride(hospitalId, schemeId, payload = {}) {
    return localApiClient.post(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/service-overrides`, payload);
  },

  updateServiceOverride(hospitalId, schemeId, id, payload = {}) {
    return localApiClient.patch(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/service-overrides/${encodeURIComponent(id)}`, payload);
  },

  removeServiceOverride(hospitalId, schemeId, id) {
    return localApiClient.delete(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/service-overrides/${encodeURIComponent(id)}`);
  },

  createDrugOverride(hospitalId, schemeId, payload = {}) {
    return localApiClient.post(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/drug-overrides`, payload);
  },

  updateDrugOverride(hospitalId, schemeId, id, payload = {}) {
    return localApiClient.patch(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/drug-overrides/${encodeURIComponent(id)}`, payload);
  },

  removeDrugOverride(hospitalId, schemeId, id) {
    return localApiClient.delete(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/drug-overrides/${encodeURIComponent(id)}`);
  },

  listServiceOverrides(hospitalId, schemeId) {
    return localApiClient.get(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/service-overrides`);
  },

  listDrugOverrides(hospitalId, schemeId) {
    return localApiClient.get(`/schemes/${encodeURIComponent(hospitalId)}/${encodeURIComponent(schemeId)}/drug-overrides`);
  },

  createClaim(payload = {}) {
    return localApiClient.post("/scheme-claims", payload);
  },

  listClaims(hospitalId, filters = {}) {
    return localApiClient.get(`/scheme-claims/${encodeURIComponent(hospitalId)}`, filters);
  },

  getClaim(hospitalId, id) {
    return localApiClient.get(`/scheme-claims/${encodeURIComponent(hospitalId)}/${encodeURIComponent(id)}`);
  },

  updateClaim(hospitalId, id, payload = {}) {
    return localApiClient.patch(`/scheme-claims/${encodeURIComponent(hospitalId)}/${encodeURIComponent(id)}`, payload);
  },

  async savePredefinedSchemeToFirestore() {
    return { success: true, message: "Local schemes are hospital-specific; no predefined seed was written." };
  },
};

export default localSchemeService;
